import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { useSearchParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Col, Form, Button } from 'react-bootstrap';
import DataTable from '../../../component/wallet/data-table/DataTable';
import DataTableToolbar from '../../../component/wallet/data-table/DataTableToolbar';
import StatusBadge from '../../../component/wallet/StatusBadge';
import CurrencyDisplay from '../../../component/wallet/CurrencyDisplay';
import { useWallets } from '../../../Hooks/wallet/use-wallets';
import { formatDate, exportToCsv } from '../../../lib/wallet/utils';
import { TRANSACTION_TYPES, DEFAULT_PAGE_SIZE } from '../../../lib/wallet/constants';

const columnHelper = createColumnHelper();

const TransactionsPage = () => {
    const { sidebar } = useContext(AppContext);
    const [searchParams] = useSearchParams();
    const defaultWalletId = searchParams.get('wallet_id') || '';

    const { loading, fetchTransactions, fetchWallets } = useWallets();
    const [transactions, setTransactions] = useState([]);
    const [wallets, setWallets] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_PAGE_SIZE, total_count: 0, total_pages: 1 });

    // Filters
    const [walletId, setWalletId] = useState(defaultWalletId);
    const [typeFilter, setTypeFilter] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const loadWallets = useCallback(async () => {
        try {
            const res = await fetchWallets();
            const w = res?.data || [];
            setWallets(w);
            if (!walletId && w.length > 0) {
                setWalletId(String(w[0].id));
            }
        } catch {
            // handled
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadWallets();
    }, [loadWallets]);

    const loadTransactions = useCallback(
        async (page = 1) => {
            if (!walletId) return;
            try {
                const res = await fetchTransactions({
                    wallet_id: walletId,
                    page,
                    limit: pagination.limit,
                    type: typeFilter || undefined,
                    from_date: fromDate || undefined,
                    to_date: toDate || undefined,
                });
                setTransactions(res?.data?.transactions || []);
                setPagination((prev) => ({
                    ...prev,
                    page,
                    ...res?.data?.pagination,
                }));
            } catch {
                // handled
            }
        },
        [walletId, typeFilter, fromDate, toDate, pagination.limit, fetchTransactions]
    );

    useEffect(() => {
        loadTransactions(1);
    }, [walletId, typeFilter, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const columns = useMemo(
        () => [
            columnHelper.accessor('id', { header: 'ID', size: 60 }),
            columnHelper.accessor('type', {
                header: 'Type',
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('amount', {
                header: 'Amount',
                cell: (info) => <CurrencyDisplay amount={info.getValue()} currencyCode="INR" />,
            }),
            columnHelper.display({
                id: 'balance_change',
                header: 'Balance',
                cell: ({ row }) => (
                    <span className="small">
                        {Number(row.original.balance_before).toFixed(2)} → {Number(row.original.balance_after).toFixed(2)}
                    </span>
                ),
            }),
            columnHelper.accessor('reference_type', { header: 'Reference', cell: (info) => info.getValue() || '—' }),
            columnHelper.accessor('description', { header: 'Description', cell: (info) => info.getValue() || '—' }),
            columnHelper.accessor('created_at', {
                header: 'Date',
                cell: (info) => formatDate(info.getValue()),
            }),
        ],
        []
    );

    const handleExport = () => {
        exportToCsv(
            transactions.map((t) => ({
                ID: t.id,
                Type: t.type,
                Amount: t.amount,
                Balance_Before: t.balance_before,
                Balance_After: t.balance_after,
                Reference: t.reference_type || '',
                Description: t.description || '',
                Date: t.created_at,
            })),
            'transactions.csv'
        );
    };

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Transaction History</h6>
            </div>
                <div className="fade-in">
            

            <DataTableToolbar
                filters={[
                    {
                        value: typeFilter,
                        onChange: setTypeFilter,
                        placeholder: 'All Types',
                        options: TRANSACTION_TYPES,
                    },
                ]}
                onExport={handleExport}
            >
                <Col xs={6} md={3} lg={2}>
                    <Form.Select
                        size="sm"
                        value={walletId}
                        onChange={(e) => setWalletId(e.target.value)}
                    >
                        <option value="">Select Wallet</option>
                        {wallets.map((w) => (
                            <option key={w.id} value={w.id}>
                                #{w.id} — {w.currency_code} ({w.wallet_type})
                            </option>
                        ))}
                    </Form.Select>
                </Col>
                <Col xs={6} md={2}>
                <Form.Label className="small fw-bold mb-1">From Date</Form.Label>
                    <Form.Control className='bg-white' size="sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} placeholder="From" />
                </Col>
                <Col xs={6} md={2}>
                <Form.Label className="small fw-bold mb-1">To Date</Form.Label>
                    <Form.Control className='bg-white' size="sm" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} placeholder="To" />
                </Col>
            </DataTableToolbar>
          <div style={{ height: 'calc(100vh - 300px)', overflow: 'auto' }}>
            <DataTable
                columns={columns}
                data={transactions}
                loading={loading}
                page={pagination.page}
                pageSize={pagination.limit}
                totalCount={pagination.total_count}
                totalPages={pagination.total_pages}
                onPageChange={(p) => loadTransactions(p)}
                onPageSizeChange={(s) => setPagination((prev) => ({ ...prev, limit: s }))}
                emptyTitle="No transactions"
                emptyDescription={walletId ? 'No transactions found for this wallet.' : 'Please select a wallet.'}
            />
            </div>
        </div>
        </div>
        </div>
    );
};

export default TransactionsPage;
